import { OrderTicket, OrderTicketList, SelectNumber } from '../../types';
import { ComboItem } from '@shared/orders/get/types';
import intlTelInput from 'intl-tel-input';
import defaultOptionalForm from '../../api/data-dummy/default-optional-form.json';
import { ReservationInfo } from '@shared/order/get/types';
import { Country } from 'intl-tel-input/data';

type CountryWithValue = Country & { value: string };

export type FormattedReservationUserInfo = {
    listFields: Array<{
        key: string;
        value: string;
        label: string;
    }>;
    userForm: {
        phoneNumber?: string | null;
        countryCode?: {
            value?: string;
            label: null;
            dialCode?: string;
            locale?: string;
            name?: string;
        };
        representativeName?: string | null;
        email?: string | null;
        remarkAnswer?: string | null;
        [key: string]: any;
    };
};

/**
 * 全角を2半角を1として文字列の長さを出力する。
 * @param {string} str
 * @returns
 */
export const getByteLength = (str: string | null) => {
    // Unicode範囲の説明:
    // 0x00～0x80: 半角英数字、半角記号
    // 0x81～0xf7: 全角文字
    // 0xf8f0: 半角カタカナの「ヶ」
    // 0xff61～0xff9f: 半角カタカナ
    // 0xf8f1～0xf8f3: 全角マイナス記号等
    if (!str) {
        return 0;
    }
    let result = 0;
    for (let i = 0; i < str.length; i++) {
        // eslint-disable-next-line no-control-regex
        if (/[^\x00-\xff]/.test(str.charAt(i))) {
            //全角文字の場合は2を加算
            result += 2;
        } else {
            //半角文字の場合は1を加算
            result += 1;
        }
    }

    // 結果を返す
    return result;
};

/**
 * チケットの購入日時（purchasedAt）に基づいて2つのチケットを比較します。
 * チケットを購入日時の降順でソートするために使用されます。
 */
export const compareTicketsByPurchasedTime = (
    ticketA: OrderTicket | OrderTicketList,
    ticketB: OrderTicket | OrderTicketList,
) => {
    if (ticketA.purchasedAt === null || ticketB.purchasedAt === null) {
        return 0;
    }
    if (ticketA.purchasedAt < ticketB.purchasedAt) {
        return 1;
    } else if (ticketA.purchasedAt > ticketB.purchasedAt) {
        return -1;
    } else {
        return 0;
    }
};

/**
 * セット券の子券をdisplayOrder順に並べて表示する。
 */
export const compareTicketsByDisplayPriority = (
    ticketA?: OrderTicket | ComboItem,
    ticketB?: OrderTicket | ComboItem,
) => {
    if (ticketA?.displayPriority == null || ticketB?.displayPriority == null) {
        return 0;
    }
    if (ticketA.displayPriority < ticketB.displayPriority) {
        return -1;
    }
    return 1;
};

/**
 * 16進カラーコードをrgbへ変換する。
 * @param {String} hex
 * @returns
 */
export const hexToRgb = (hex: string) => {
    // 16進数のカラーコードから#を取り除く
    const h = hex.replace(/^#/, '');

    // 16進数をRGBに変換
    const bigint = parseInt(h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    // RGB値をオブジェクトで返す
    return { r, g, b };
};

/**
 * 合計金額を計算する。
 * @param {Object} number
 * @param {Object} unitPrice
 * @returns
 */
export const calcTotalPrice = (number?: SelectNumber | null, unitPrice?: { [k: string]: number }) => {
    if (!unitPrice || !number) {
        return 0;
    }
    const totalPrice = Object.keys(number)
        .map((key) => {
            const price = unitPrice[key] ?? 0;
            const count = number[key] ?? 0;
            return price * count;
        })
        .reduce((a, b) => a + b, 0);

    return totalPrice;
};

/**
 * 指定された回数まで、非同期関数のエラー時にリトライする。
 * @param {Function} operation - The operation to be retried, should return a Promise.
 * @param {Number} retryCount
 * @returns
 */
export const retry = async (operation: () => Promise<void> = async () => {}, retryCount: number): Promise<any> => {
    // Ensure retryCount is a positive integer
    if (retryCount < 1 || !Number.isInteger(retryCount)) {
        throw new Error('Invalid retryCount. It should be a positive integer.');
    }

    try {
        return await operation();
    } catch (error) {
        if (retryCount === 1) {
            // If reached the maximum retry count, throw the last error
            throw error;
        }

        // Retry with a reduced retryCount
        return await retry(operation, retryCount - 1);
    }
};

/**
 * @param {Function} operation - リトライされる非同期関数。Promise を返す必要があります。
 * @param {Number} retryCount - リトライの最大回数。正の整数である必要があります。
 * @param {Number} timeout - リトライ前のタイムアウト（ミリ秒単位）。初回リトライの前に適用されます。(オプション)
 * @param {Function} shouldStopRetry - リトライを停止するかどうかを判断する関数。結果が true の場合、リトライが停止されます。(オプション)
 * @returns
 */
export const retryWithTimeoutAndCondition = async (
    operation: () => Promise<any> = async () => {},
    retryCount: number,
    timeout: number,
    shouldStopRetry?: any,
): Promise<OrderTicket | undefined> => {
    if (retryCount < 1 || !Number.isInteger(retryCount)) {
        throw new Error('Invalid retryCount. It should be a positive integer.');
    }

    let timeoutId;
    let result;

    try {
        result = await operation();

        if (typeof shouldStopRetry !== 'function' || shouldStopRetry(result)) {
            return result;
        }

        if (timeout != null) {
            await new Promise((resolve) => (timeoutId = setTimeout(resolve, timeout)));
        }

        return await retryWithTimeoutAndCondition(operation, retryCount - 1, timeout, shouldStopRetry);
    } catch (error) {
        console.error(error);
    } finally {
        if (timeout != null) {
            clearTimeout(timeoutId);
        }
    }
};

export const formatPriceWithMinMax = (
    { minPrice, maxPrice }: { minPrice?: number | null; maxPrice?: number | null },
    callback: (params: any, unit: string) => string,
) => {
    if (minPrice === maxPrice) {
        return callback(minPrice, 'JPY');
    }
    return `${(minPrice ?? 0) > 0 ? callback(minPrice, 'JPY') : ''}${
        (minPrice ?? 0) > 0 && (maxPrice ?? 0) > 0 ? ' ~ ' : ''
    }${(maxPrice ?? 0) > 0 ? callback(maxPrice, 'JPY') : ''}`;
};

export const formatReservationUserInfo = (reservationInfo?: ReservationInfo | null): FormattedReservationUserInfo => {
    const visibleOptionalForm = reservationInfo?.reservationForm?.filter(
        (field: Record<string, any>) => field.isVisible,
    );
    const combined: Record<string, any> = {};
    defaultOptionalForm?.forEach((item: Record<string, any>) => {
        combined[item.key] = item;
    });
    visibleOptionalForm?.forEach((item: Record<string, any>) => {
        combined[item.key] = item;
    });
    const optionalForm = Object.values(combined);

    const countryData = intlTelInput.getCountryData();
    const newData = countryData?.reduce((acc: CountryWithValue[], item: Country) => {
        if (item.areaCodes && item.areaCodes.length > 0) {
            item.areaCodes.forEach((areaCode, index) => {
                acc.push({
                    ...item,
                    dialCode: `+${item.dialCode}-${areaCode}`,
                    value: `${item.dialCode}-${areaCode}${item.iso2}${index}`,
                });
            });
        } else {
            acc.push({
                ...item,
                dialCode: `+${item.dialCode}`,
                value: `${item.dialCode}-${item.iso2}`,
            });
        }
        return acc;
    }, []);

    let userForm = reservationInfo?.userForm;
    const phoneNumber = userForm?.phoneNumber;
    const index = phoneNumber?.lastIndexOf('-');
    const dialCode = phoneNumber?.substring(0, index);
    const phoneNumberFormat = phoneNumber?.substring((index ?? 0) + 1);
    const country = newData.find((country) => country.dialCode === dialCode);

    userForm = {
        ...userForm,
        phoneNumber: phoneNumberFormat ?? null,
        countryCode: {
            value: country?.value,
            label: null,
            dialCode: country?.dialCode,
            locale: country?.iso2,
            name: country?.name,
        },
        representativeName: userForm?.representativeName ?? null,
        email: userForm?.email ?? null,
        remarkAnswer: userForm?.remarkAnswer ?? null,
    };

    const listFields: { key: string; value: string; label: string }[] = [];
    optionalForm?.forEach(({ label, key }: { label: string; key: string }) => {
        const field = { key: key, value: userForm[key], label: label };
        listFields.push(field);
    });

    return {
        listFields,
        userForm,
    };
};
